import {
  CensusUploadJobChannel,
  CensusUploadJobDto,
  CensusUploadQueuedDto,
  CensusUploadQueueRequestDto,
} from '../api/dto/census-upload-job.dto'
import { CensusClientFilter } from '../types/census-client-filter'

// A 202 body from an upload; a census file has its own `status`, so look at both
export const isQueuedUpload = (
  result: unknown
): result is CensusUploadQueuedDto =>
  typeof result === 'object' &&
  result !== null &&
  'jobId' in result &&
  'status' in result &&
  (result as { status: unknown }).status === 'QUEUED'

export const CENSUS_UPLOAD_STAGE_LABEL: Record<string, string> = {
  DOWNLOADING: 'Reading the file',
  MAPPING: 'Mapping the data',
  VALIDATING: 'Validating records',
  DEDUPING: 'Checking duplicates',
  SAVING: 'Saving',
}

export const describeUploadStage = (stage: string | null): string | null =>
  stage ? (CENSUS_UPLOAD_STAGE_LABEL[stage] ?? null) : null

// The Queue takes the same filters as Drafts
export const buildCensusUploadQueueFilters = (
  clientIds: string[],
  filter?: CensusClientFilter,
  // CMv2 lists manual uploads; the HRIS page lists AMA jobs
  channel: CensusUploadJobChannel[] = ['MANUAL_UPLOAD']
): CensusUploadQueueRequestDto => ({
  clientPublicKey: clientIds,
  channel,
  includeTestClients: filter?.includeTestCompanies,
  dateFrom: filter?.dateFrom,
  dateTo: filter?.dateTo,
})

export const CENSUS_QUEUE_POLL_INTERVAL_MS = 15000

// Only queued or processing uploads can still move; failed ones wait for a person
const isActiveUpload = (job: CensusUploadJobDto): boolean =>
  job.status === 'QUEUED' || job.status === 'PROCESSING'

export const hasActiveUploads = (jobs: CensusUploadJobDto[]): boolean =>
  jobs.some(isActiveUpload)

export const getActiveUploadIds = (jobs: CensusUploadJobDto[]): Set<string> =>
  new Set(jobs.filter(isActiveUpload).map((job) => job.id))

// A job that was running and no longer is: it completed (left the list) or failed
export const hasFinishedUploads = (
  previousActiveIds: Set<string>,
  currentActiveIds: Set<string>
): boolean =>
  Array.from(previousActiveIds).some((id) => !currentActiveIds.has(id))

export type ActiveUploadsSnapshot = { page: number; ids: Set<string> }

// Only a refresh of the same page can tell a job finished; another page lists other jobs
export const hasFinishedUploadsOnPage = (
  previous: ActiveUploadsSnapshot | undefined,
  page: number,
  currentActiveIds: Set<string>
): boolean =>
  previous?.page === page && hasFinishedUploads(previous.ids, currentActiveIds)
